import React, { useState, useEffect, useRef } from 'react';
import { Search, X, ChevronDown, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AreaAutocomplete - Autocomplete input for CAPES knowledge areas
 * Shows only "pequenas áreas" (most specific) with full path
 */
export const AreaAutocomplete = ({ 
  value = [], 
  onChange, 
  placeholder = "Digite para buscar áreas...",
  maxSelections = 5,
  label = "Áreas de Interesse"
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Search areas as user types
  useEffect(() => {
    const searchAreas = async () => {
      if (query.length < 2) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/areas/search?q=${encodeURIComponent(query)}`);
        const data = await response.json();
        setResults(data.results || []);
      } catch (error) {
        console.error('Error searching areas:', error);
        setResults([]);
      }
      setLoading(false);
    };

    const debounce = setTimeout(searchAreas, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (area) => {
    if (value.length >= maxSelections) {
      alert(`Você pode selecionar no máximo ${maxSelections} áreas.`);
      return;
    }

    if (!value.includes(area.pequena_area)) {
      onChange([...value, area.pequena_area]);
    }
    setQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleRemove = (areaToRemove) => {
    onChange(value.filter(a => a !== areaToRemove));
  };

  return (
    <div className="w-full" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          <span className="text-gray-400 font-normal ml-2">({value.length}/{maxSelections})</span>
        </label>
      )}

      {/* Selected areas */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {value.map((area, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1 bg-[#002FA7]/10 text-[#002FA7] rounded-full text-sm"
            >
              {area}
              <button
                type="button"
                onClick={() => handleRemove(area)}
                className="hover:bg-[#002FA7]/20 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none transition-colors"
            disabled={value.length >= maxSelections}
            data-testid="area-autocomplete-input"
          />
          {loading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin h-4 w-4 border-2 border-[#002FA7] border-t-transparent rounded-full"></div>
            </div>
          )}
        </div>

        {/* Results dropdown */}
        {isOpen && results.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {results.map((result, index) => {
              const isSelected = value.includes(result.pequena_area);
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => !isSelected && handleSelect(result)}
                  disabled={isSelected}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors ${
                    isSelected ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  data-testid={`area-option-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        {result.pequena_area}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {result.grande_area} › {result.subarea}
                      </div>
                    </div>
                    {isSelected && <Check className="h-4 w-4 text-green-600" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {isOpen && query.length >= 2 && results.length === 0 && !loading && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
            Nenhuma área encontrada para "{query}"
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400 mt-1">
        Digite pelo menos 2 caracteres para buscar
      </p>
    </div>
  );
};

/**
 * AreaHierarchySelect - Cascading select for Grande Área > Subárea
 * Used for filtering calls in the dashboard
 */
export const AreaHierarchySelect = ({ 
  value = { grandeArea: '', subarea: '' },
  onChange,
  showSubarea = true
}) => {
  const [grandesAreas, setGrandesAreas] = useState([]);
  const [subareas, setSubareas] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load grandes áreas on mount
  useEffect(() => {
    const loadGrandesAreas = async () => {
      try {
        const response = await fetch(`${API_URL}/api/areas/grandes-areas`);
        const data = await response.json();
        setGrandesAreas(data.grandes_areas || []);
      } catch (error) {
        console.error('Error loading grandes áreas:', error);
      }
    };
    loadGrandesAreas();
  }, []);

  // Load subáreas when grande área changes
  useEffect(() => {
    const loadSubareas = async () => {
      if (!value.grandeArea) {
        setSubareas([]);
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/areas/subareas/${encodeURIComponent(value.grandeArea)}`);
        const data = await response.json();
        setSubareas(data.subareas || []);
      } catch (error) {
        console.error('Error loading subáreas:', error);
        setSubareas([]);
      }
      setLoading(false);
    };
    loadSubareas();
  }, [value.grandeArea]);

  const handleGrandeAreaChange = (e) => {
    onChange({ grandeArea: e.target.value, subarea: '' });
  };

  const handleSubareaChange = (e) => {
    onChange({ ...value, subarea: e.target.value });
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3">
      {/* Grande Área select */}
      <div className="flex-1">
        <label className="block text-xs font-medium text-gray-500 mb-1">Grande Área</label>
        <select
          value={value.grandeArea}
          onChange={handleGrandeAreaChange}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none text-sm"
          data-testid="grande-area-select"
        >
          <option value="">Todas as áreas</option>
          {grandesAreas.map((area, index) => (
            <option key={index} value={area}>{area}</option>
          ))}
        </select>
      </div>

      {/* Subárea select */}
      {showSubarea && value.grandeArea && (
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-500 mb-1">Subárea</label>
          <select
            value={value.subarea}
            onChange={handleSubareaChange}
            disabled={loading || subareas.length === 0}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none text-sm disabled:bg-gray-100"
            data-testid="subarea-select"
          >
            <option value="">Todas as subáreas</option>
            {subareas.map((subarea, index) => (
              <option key={index} value={subarea}>{subarea}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

/**
 * AreaFilterAutocomplete - Single-select autocomplete for filtering
 * Used in dashboard filters - simpler than AreaAutocomplete
 */
export const AreaFilterAutocomplete = ({ 
  value = '',
  onChange,
  placeholder = "Digite para buscar área...",
  label = "Área"
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Search areas as user types
  useEffect(() => {
    const searchAreas = async () => {
      if (query.length < 2) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/areas/search?q=${encodeURIComponent(query)}`);
        const data = await response.json();
        setResults(data.results || []);
      } catch (error) {
        console.error('Error searching areas:', error);
        setResults([]);
      }
      setLoading(false);
    };

    const debounce = setTimeout(searchAreas, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (area) => {
    onChange(area.pequena_area, area.grande_area, area.subarea);
    setQuery('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('', '', '');
    setQuery('');
  };

  return (
    <div className="w-full" ref={dropdownRef}>
      {label && (
        <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      )}

      {/* Selected area badge */}
      {value && (
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#002FA7]/10 text-[#002FA7] rounded-full text-sm">
            {value}
            <button
              type="button"
              onClick={handleClear}
              className="hover:bg-[#002FA7]/20 rounded-full p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        </div>
      )}

      {/* Search input */}
      {!value && (
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              placeholder={placeholder}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none transition-colors text-sm"
              data-testid="area-filter-input"
            />
            {loading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin h-4 w-4 border-2 border-[#002FA7] border-t-transparent rounded-full"></div>
              </div>
            )}
          </div>

          {/* Results dropdown */}
          {isOpen && results.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {results.map((result, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSelect(result)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                  data-testid={`area-filter-option-${index}`}
                >
                  <div className="font-medium text-gray-900 text-sm">
                    {result.pequena_area}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {result.grande_area} › {result.subarea}
                  </div>
                </button>
              ))}
            </div>
          )}

          {isOpen && query.length >= 2 && results.length === 0 && !loading && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-center text-gray-500 text-sm">
              Nenhuma área encontrada
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AreaAutocomplete;
